import { ONCHAIN_GC_TIME, ONCHAIN_REFETCH_INTERVAL, ONCHAIN_STALE_TIME } from '@/config/consts'
import {
  E_EARN_GOVERNED_PERSPECTIVE,
  E_EARN_VAULT_CHAIN_ID,
  SHOW_ALL_EVAULTS,
  USD_REFERENCE_TOKEN,
} from '@/config/euler-contracts'
import {
  type EulerIndexerStrategy,
  type NormalizedTokenInfo,
  fetchEVaultsOnchain,
  getTokenFromMap,
  useEulerIndexerEVaults,
  useTokensInfo,
  useVaultUserBalances,
} from '@/domain/euler'
import { useBatchAssetPrices } from '@/domain/euler/oracle/useAssetPrice'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { useQuery } from '@tanstack/react-query'
import { eulerEarnGovernedPerspectiveAbi } from 'abis/evm/eulerEarnGovernedPerspective'
import { useMemo } from 'react'
import { Address, formatUnits, getAddress } from 'viem'
import { usePublicClient, useReadContract } from 'wagmi'
import { useBatchEVaultAPY } from './useBatchEVaultAPY'

export { E_EARN_VAULT_CHAIN_ID, USD_REFERENCE_TOKEN }
export const EULER_EARN_GOVERNED_PERSPECTIVE_ADDRESS = E_EARN_GOVERNED_PERSPECTIVE

export interface WalletBreakdown {
  nativeUsd: number
  wrappedUsd: number
  nativeAmount: number
  wrappedAmount: number
}

export interface EVaultData {
  address: Address
  name: string
  symbol: string
  assetSymbol?: string
  underlyingTokenInfo?: NormalizedTokenInfo
  decimals: number
  totalSupply: NormalizedUnitNumber
  totalAssets: NormalizedUnitNumber
  tvlUsd?: NormalizedUnitNumber
  underlyingAsset?: Address
  userBalance?: NormalizedUnitNumber
  userBalanceUsd?: NormalizedUnitNumber
  walletBalance?: NormalizedUnitNumber
  walletBreakdown?: WalletBreakdown
  token?: Token
  supplyAPY?: number
  supplyApy30d?: number | null
  sharePrice?: NormalizedUnitNumber
  exchangeRate?: number
  owner?: Address
  strategies?: EulerIndexerStrategy[]
}

export interface UseEVaultsResult {
  vaults: EVaultData[]
  isLoading: boolean
  error: Error | null
}

export function useEVaults(userAddress?: Address): UseEVaultsResult {
  // Step 1: Fetch verified vaults list from governed perspective contract (onchain)
  const {
    data: verifiedVaultsList,
    isPending: isPendingVerified,
    error: verifiedError,
  } = useReadContract({
    address: EULER_EARN_GOVERNED_PERSPECTIVE_ADDRESS,
    abi: eulerEarnGovernedPerspectiveAbi,
    functionName: 'verifiedArray',
    chainId: E_EARN_VAULT_CHAIN_ID,
    query: {
      staleTime: 60_000, // Cache for 1 minute
      refetchInterval: 60_000, // Refetch every 1 minute
    },
  })

  // Create a Set of verified vault addresses for efficient lookup
  const verifiedAddressSet = useMemo(
    () => new Set((verifiedVaultsList as Address[] | undefined)?.map((addr) => getAddress(addr).toLowerCase()) ?? []),
    [verifiedVaultsList],
  )

  // Step 2: Fetch all vaults from Euler Indexer
  const {
    data: indexerVaultsMap,
    isPending: isPendingIndexer,
    isError: isIndexerError,
    error: indexerError,
  } = useEulerIndexerEVaults(E_EARN_VAULT_CHAIN_ID)

  // Filter indexer vaults based on SHOW_ALL_EVAULTS setting
  const indexerVaults = useMemo(() => {
    if (!indexerVaultsMap) return []

    if (SHOW_ALL_EVAULTS) {
      return Array.from(indexerVaultsMap.values()) // Show all vaults
    }

    return Array.from(indexerVaultsMap.values()).filter((vault) =>
      verifiedAddressSet.has(getAddress(vault.address).toLowerCase()),
    ) // Show only verified vaults
  }, [indexerVaultsMap, verifiedAddressSet])

  // Step 2b: ONCHAIN FALLBACK - If indexer fails, fetch vault details onchain
  const publicClient = usePublicClient({ chainId: E_EARN_VAULT_CHAIN_ID })
  const verifiedVaultAddresses = (verifiedVaultsList as Address[] | undefined) ?? []
  const shouldFetchOnchain = isIndexerError && !isPendingIndexer && verifiedVaultAddresses.length > 0 && !!publicClient

  const {
    data: onchainVaultsMap,
    isPending: isPendingOnchain,
    error: onchainError,
  } = useQuery({
    queryKey: ['evaults-onchain-fallback', E_EARN_VAULT_CHAIN_ID, verifiedVaultAddresses],
    queryFn: async () => {
      if (!publicClient) throw new Error('No publicClient provided')
      const result = await fetchEVaultsOnchain(publicClient, E_EARN_VAULT_CHAIN_ID, verifiedVaultAddresses)
      return result
    },
    enabled: shouldFetchOnchain,
    staleTime: ONCHAIN_STALE_TIME,
    gcTime: ONCHAIN_GC_TIME,
    refetchInterval: ONCHAIN_REFETCH_INTERVAL,
    retry: 2,
  })

  // Use onchain data if indexer failed
  const onchainVaults = useMemo(
    () => (onchainVaultsMap ? Array.from(onchainVaultsMap.values()) : []),
    [onchainVaultsMap],
  )

  const vaultsToUse = useMemo(
    () => (indexerVaults.length > 0 ? indexerVaults : onchainVaults),
    [indexerVaults, onchainVaults],
  )

  // Memoize vault addresses to prevent repeated fetching
  const vaultAddresses = useMemo(() => vaultsToUse.map((v) => v.address), [vaultsToUse])

  // Step 2.5: Fetch live APY from contracts for all vaults
  // This provides real-time APY calculated from strategy interest rates
  // Always enabled - indexer apyCurrent is only used as fallback
  const {
    apyMap,
    strategyMap,
    isPending: isPendingLiveApy,
  } = useBatchEVaultAPY({
    vaultAddresses,
    enabled: vaultAddresses.length > 0,
  })

  // Step 3: Fetch underlying asset addresses from indexer for token info
  const underlyingAssetAddresses = useMemo(
    () => vaultsToUse.filter((v) => v.asset).map((v) => v.asset as Address),
    [vaultsToUse],
  )
  const parallelAssetAddresses = useMemo(
    () => vaultsToUse.map((v) => (v.asset as Address | undefined) ?? undefined),
    [vaultsToUse],
  )

  // Collect lending vault (strategy) addresses so VaultLens can price them
  // and we reuse those prices for Earn vaults sharing the same underlying asset.
  // Priority: indexer strategies → on-chain strategies from useBatchEVaultAPY.
  const lendingVaultAddresses = useMemo(() => {
    const addrs: Address[] = []
    for (const v of vaultsToUse) {
      const indexerStrats = v.strategies ?? []
      if (indexerStrats.length > 0) {
        for (const s of indexerStrats) {
          if (s.strategy) addrs.push(s.strategy)
        }
      } else {
        const onchainStrats = strategyMap.get(v.address)
        if (onchainStrats) {
          addrs.push(...onchainStrats)
        }
      }
    }
    return addrs
  }, [vaultsToUse, strategyMap])

  // Use Euler indexer for token info (symbol, decimals, logoURI) with onchain fallback
  const { tokens: indexerTokens, isPending: isPendingTokenInfo } = useTokensInfo(
    E_EARN_VAULT_CHAIN_ID,
    underlyingAssetAddresses,
  )

  const { assetPriceMap, isPending: isPendingAssetPrices } = useBatchAssetPrices({
    vaultAddresses,
    assetAddresses: parallelAssetAddresses,
    lendingVaultAddresses,
    chainId: E_EARN_VAULT_CHAIN_ID,
    enabled: vaultsToUse.length > 0,
  })

  // Step 4: Fetch user-specific data (balances) using shared hook
  const { balances: userBalances } = useVaultUserBalances({
    vaultAddresses,
    underlyingAssets: vaultsToUse.map((v) => v.asset ?? undefined),
    decimals: vaultsToUse.map((v) => v.assetDecimals ?? v.decimals),
    userAddress,
    chainId: E_EARN_VAULT_CHAIN_ID,
    enabled: !!userAddress,
  })

  // Step 5: Process and combine all data
  const vaults: EVaultData[] = vaultsToUse.map((indexerVault) => {
    const decimals = indexerVault.decimals
    const assetDecimals = indexerVault.assetDecimals ?? decimals

    // Get underlying asset info from Euler indexer (with onchain fallback)
    const underlyingTokenInfo = getTokenFromMap(indexerTokens, indexerVault.asset ?? undefined)
    const assetSymbol = underlyingTokenInfo?.symbol ?? indexerVault.assetSymbol ?? undefined

    // Calculate share price from totalAssets and totalShares
    let sharePrice: NormalizedUnitNumber | undefined
    let exchangeRate: number | undefined

    if (indexerVault.totalShares > 0n && indexerVault.totalAssets > 0n) {
      // Exchange rate = totalAssets / totalShares (in asset decimals)
      exchangeRate = Number(indexerVault.totalAssets) / Number(indexerVault.totalShares)
      sharePrice = NormalizedUnitNumber(exchangeRate.toString())
    }

    // Get user balances from shared hook
    const userBalanceData = userBalances.get(indexerVault.address)
    const userBalanceRaw = userBalanceData?.vaultBalance
    const walletBalanceRaw = userBalanceData?.walletBalance
    const rawBreakdown = userBalanceData?.walletBalanceBreakdown

    // Convert TVL to NormalizedUnitNumber
    // Priority: indexer totalAssetsUSD (if > 0) → calculated from totalAssets * oracle price → 0 if no deposits
    const totalAssetsNormalized = Number(formatUnits(indexerVault.totalAssets, assetDecimals))
    let tvlUsd: NormalizedUnitNumber | undefined

    if (indexerVault.totalAssetsUSD !== undefined && indexerVault.totalAssetsUSD > 0) {
      // Use indexer-provided TVL USD (only if positive - indexer defaults to 0 when unknown)
      tvlUsd = NormalizedUnitNumber(indexerVault.totalAssetsUSD.toString())
    } else if (totalAssetsNormalized === 0) {
      // No deposits = $0 TVL (not a skeleton)
      tvlUsd = NormalizedUnitNumber('0')
    } else if (indexerVault.asset) {
      // Fallback: calculate from totalAssets * oracle price
      const assetPrice = assetPriceMap.get(indexerVault.asset.toLowerCase())
      if (assetPrice && assetPrice > 0) {
        tvlUsd = NormalizedUnitNumber((totalAssetsNormalized * assetPrice).toString())
      }
    }

    return {
      address: indexerVault.address,
      name: indexerVault.name,
      symbol: indexerVault.symbol,
      assetSymbol,
      underlyingTokenInfo,
      decimals,
      totalSupply: NormalizedUnitNumber(formatUnits(indexerVault.totalShares, decimals)),
      totalAssets: NormalizedUnitNumber(formatUnits(indexerVault.totalAssets, assetDecimals)),
      tvlUsd,
      underlyingAsset: indexerVault.asset ?? undefined,
      userBalance:
        userBalanceRaw !== undefined ? NormalizedUnitNumber(formatUnits(userBalanceRaw, decimals)) : undefined,
      walletBalance:
        walletBalanceRaw !== undefined && assetDecimals !== undefined
          ? NormalizedUnitNumber(formatUnits(walletBalanceRaw, assetDecimals))
          : undefined,
      walletBreakdown: (() => {
        if (!rawBreakdown || assetDecimals === undefined) return undefined
        const nativeAmount = Number(rawBreakdown.nativeBalanceFormatted)
        const wrappedAmount = Number(rawBreakdown.wrappedBalanceFormatted)
        if (nativeAmount <= 0 && wrappedAmount <= 0) return undefined
        const assetPrice = indexerVault.asset ? assetPriceMap.get(indexerVault.asset.toLowerCase()) ?? 0 : 0
        return {
          nativeAmount,
          wrappedAmount,
          nativeUsd: nativeAmount * assetPrice,
          wrappedUsd: wrappedAmount * assetPrice,
        }
      })(),
      // Use APY from batch calculation (progressive) or fallback to vault data
      // For onchain data: apyMap will progressively populate as strategies are calculated
      // For indexer data: use the apyCurrent from indexer directly
      supplyAPY: apyMap.get(indexerVault.address) ?? indexerVault.apyCurrent ?? undefined,
      supplyApy30d: indexerVault.apy30d,
      sharePrice,
      exchangeRate,
      owner: indexerVault.owner ?? undefined,
      strategies:
        indexerVault.strategies && indexerVault.strategies.length > 0
          ? indexerVault.strategies
          : strategyMap.get(indexerVault.address)?.map((addr) => ({ strategy: addr }) as EulerIndexerStrategy) ??
            undefined,
    }
  })

  // Show loading only for critical data needed to render cards
  // APY and TVL will show skeletons while loading in individual cards
  // When SHOW_ALL_EVAULTS is true, don't wait for verified list
  const hasVaultsWaitingForTvlPrice = vaults.some(
    (vault) => vault.tvlUsd === undefined && vault.underlyingAsset && vault.totalAssets.gt(0),
  )
  const hasVaultsWaitingForTokenInfo = vaults.some((vault) => vault.underlyingAsset && !vault.underlyingTokenInfo)
  const hasVaultsWaitingForLiveApy = vaultAddresses.length > 0 && isPendingLiveApy
  const baseLoading =
    isPendingIndexer ||
    (shouldFetchOnchain && isPendingOnchain) ||
    (hasVaultsWaitingForTokenInfo && isPendingTokenInfo) ||
    hasVaultsWaitingForLiveApy ||
    (hasVaultsWaitingForTvlPrice && isPendingAssetPrices)
  const isLoading = SHOW_ALL_EVAULTS ? baseLoading : isPendingVerified || baseLoading

  // Error handling based on SHOW_ALL_EVAULTS setting
  const hasVerifiedVaults = verifiedVaultAddresses.length > 0
  const bothFailed = isIndexerError && shouldFetchOnchain && !!onchainError && !onchainVaultsMap

  const error = SHOW_ALL_EVAULTS
    ? bothFailed
      ? onchainError ?? indexerError
      : null // When showing all, only error if both indexer and onchain fail
    : verifiedError ?? (bothFailed && hasVerifiedVaults ? onchainError ?? indexerError : null) // When filtering by verified, also check verified list errors

  return {
    vaults,
    isLoading,
    error: error as Error | null,
  }
}
